import { api } from '../../shared/api/client'

export type AssetKind = 'photogen_template' | 'static_image'
export type FunnelStatus = 'draft' | 'testing' | 'published' | 'archived'

export interface ContentAsset {
  id: number
  name: string
  kind: AssetKind
  status: 'staging' | 'ready' | 'invalid' | 'archived'
  source_format: 'psd' | 'package' | 'image' | 'legacy'
  width?: number | null
  height?: number | null
  validation_errors?: string[]
  validation_warnings?: string[]
  deduplicated?: boolean
  /**
   * Что шаблон ждёт на вход и куда кладёт результат — считает движок
   * (`content_funnels/input_modes.py`). Пустое значение = решает рендерер.
   */
  auto_input_mode?: string | null
  auto_output_folder?: string | null
  auto_settings?: Record<string, unknown>
}

export interface PackageValidation {
  ok: boolean
  errors: string[]
  warnings: string[]
  normalized_config?: Record<string, unknown>
}

export interface FunnelSummary {
  id: number
  name: string
  description?: string | null
  created_at: string
  updated_at: string
  version_count: number
  latest_version?: number | null
  latest_version_id?: number | null
  latest_status?: FunnelStatus | null
  published_version_id?: number | null
  first_asset_id?: number | null
  first_item_type?: FunnelItem['item_type'] | null
  /** Набор визуалов первого слайда: превью аудиторных воронок должно отличаться. */
  first_variant?: string | null
  marketplace?: string | null
  cabinet?: string | null
  seller_id?: string | null
  category_id?: number | null
  category_prefix?: string | null
  category_name?: string | null
  design_type?: string | null
  occasion?: string | null
  persona?: string | null
  brand?: string | null
  theme?: string | null
  gender?: string | null
  age?: string | null
  rule_count?: number
  source_system?: string | null
  source_key?: string | null
}

export interface FunnelItem {
  id?: number
  position?: number
  item_type: 'generated' | 'static' | 'manual' | 'ai_scene' | 'ai_inpaint'
  asset_id?: number | null
  asset_name?: string
  enabled: boolean
  input_mode?: string | null
  output_folder?: string | null
  settings?: Record<string, unknown>
}

export interface FunnelRule {
  id?: number
  marketplace: string
  cabinet: string
  seller_id?: string | null
  category_id?: number | null
  category_prefix?: string | null
  category_name?: string | null
  design_type?: string | null
  occasion?: string | null
  persona?: string | null
  brand?: string | null
  theme?: string | null
  gender?: string | null
  /** Возраст аудитории макета; вместе с полом выбирает гендерный набор визуалов. */
  age?: string | null
  priority: number
  enabled: boolean
}

export interface FunnelVersion {
  id: number
  funnel_id: number
  funnel_name: string
  funnel_description?: string | null
  source_system?: string | null
  source_key?: string | null
  version_no: number
  status: FunnelStatus
  revision: number
  updated_at: string
  notes?: string | null
  items: FunnelItem[]
  rules: FunnelRule[]
}

/**
 * Ответ публикации. requires_confirmation с code='test_drive_missing' — не
 * отказ, а предупреждение: генерация не проходила тест-драйв, и публикацию
 * можно повторить с skipTestDrive.
 */
export interface PublishResult {
  ok: boolean
  errors?: string[]
  warnings?: string[]
  conflicts?: unknown[]
  code?: string
  requires_confirmation?: boolean
}

/**
 * Что держит воронку в истории производства: цели прогонов конвейера и
 * ревизии карточек МП. FK у них ON DELETE RESTRICT, поэтому удаление сначала
 * спрашивает согласие отвязать эти ссылки.
 */
export interface FunnelHistoryLinks {
  targets: number
  runs: number
  active_runs: number
  card_revisions: number
  cards: number
}

export interface DeleteFunnelResult {
  ok: boolean
  id: number
  /** Воронка PHOTOGEN не удаляется, а уходит в архив. */
  archived?: boolean
  code?: string
  requires_confirmation?: boolean
  blocked_by?: FunnelHistoryLinks
  detached?: { targets: number; card_revisions: number }
}

/** Что уедет в архив — считается до его сборки. */
export interface ExportPreview {
  funnels: number
  assets: number
  files_bytes: number
  published: number
  warnings: string[]
  items: Array<{ name: string; status: FunnelStatus | null; slides: number; rules: number }>
}

export interface ImportFunnelReport {
  name: string
  action: 'created' | 'updated' | 'skipped' | 'failed'
  status?: string | null
  version_no?: number | null
  slides?: number
  rules?: number
  errors: string[]
  warnings: string[]
}

export interface ImportReport {
  ok: boolean
  dry_run: boolean
  manifest: {
    exported_at?: string
    include_files?: boolean
    funnels?: number
    assets?: number
    files_bytes?: number
  }
  assets: { total: number; matched: number; linked: number; created: number; missing: number }
  errors: string[]
  funnels: ImportFunnelReport[]
  created?: number
  updated?: number
  skipped?: number
  failed?: number
  published?: number
}

/** Отчёт массовой операции над каталогом воронок. */
export interface BulkFunnelReport {
  ok: boolean
  created?: number
  updated?: number
  failed?: number
  added?: number
  funnels: Array<{
    funnel_id: number
    name: string | null
    action: 'created' | 'updated' | 'failed'
    version_no?: number
    slides?: number
    rules?: number
    added?: number
    drafted?: boolean
    errors: string[]
  }>
}

/** Разбор воронки на аудиторные по наборам визуалов PHOTOGEN. */
export interface AudienceSplitReport {
  ok: boolean
  dry_run: boolean
  error?: string
  available_suffixes: string[]
  source: { funnel_id: number; version_id: number; name: string; status: string }
  funnels: Array<{
    audience: string
    name: string
    suffix: string
    action?: 'created' | 'updated'
    status?: string
    gender?: string
    ages?: string[]
    rules: number
    warnings?: string[]
  }>
  created?: number
  updated?: number
  published?: number
}

function unwrap<T>(response: any): T {
  return (response.data?.data ?? response.data) as T
}

export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error)
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '')
    reader.readAsDataURL(file)
  })
}

/**
 * Общие (не генерируемые) файлы воронки в публичном каталоге hdd.
 *
 * Такой файл лежит один на воронку и кабинет, а карточки всех макетов
 * ссылаются на один адрес — вместо копии под именем каждого артикула.
 */
export interface FunnelFile {
  rel: string
  url: string
  cabinet: string
  position: number
  item_id?: number | null
  version_id?: number | null
  present: boolean
}

export interface FunnelFilesListing {
  funnel_id: number
  public_dir: string | null
  root: string | null
  files: FunnelFile[]
  /** Слайды состава, файлов которых в папке ещё нет. */
  missing: string[]
  /** Файлы в папке, которых нет в составе воронки. */
  orphans: string[]
}

export interface FunnelFilesSyncSummary {
  created: number
  replaced: number
  kept: number
  /** Файлы отработавших версий: не обновляем, но и не убираем. */
  history: number
  archived: number
  orphans: number
  errors: number
}

export interface FunnelFilesSyncReport {
  funnel_id: number
  public_dir: string
  created: string[]
  replaced: string[]
  kept: string[]
  history: string[]
  archived: string[]
  orphans: string[]
  errors: Array<{ file: string; error: string }>
  summary: FunnelFilesSyncSummary
}

export interface FunnelFilesSyncTotals {
  funnels: number
  totals: FunnelFilesSyncSummary
  reports: Array<FunnelFilesSyncReport | { funnel_id: number; error: string }>
}

export interface FunnelFilesAdoptReport {
  funnel_id: number
  version_id: number
  adopted: Array<{ rel: string; asset_id: number; item_id: number; version_id: number }>
  errors: Array<{ file: string; error: string }>
}

/**
 * Адрес превью ресурса библиотеки для общего транспорта картинок.
 *
 * Раньше превью приезжало base64-строкой внутри JSON: браузер такой ответ за
 * картинку не считает и в кэш не кладёт, а каждая плитка шла в сеть отдельным
 * запросом без ограничения темпа. Теперь это обычный медиа-адрес, и им
 * занимается AuthorizedMediaImage — с общей очередью, кэшем blob-ов и
 * заголовком кэша от гейтвея.
 *
 * variant — набор визуалов PHOTOGEN (_boy, _woman, …); пусто — base.png.
 */
export function contentAssetThumbnailUrl(id: number, variant?: string): string {
  const path = `/api/v1/content-assets/${id}/thumbnail`
  return variant ? `${path}?variant=${encodeURIComponent(variant)}` : path
}

export const managedFunnelsAPI = {
  async listFunnels(): Promise<FunnelSummary[]> {
    const response = await api.get('/content-funnels')
    return unwrap<{ funnels: FunnelSummary[] }>(response).funnels || []
  },
  async createFunnel(payload: { name: string; description?: string }) {
    return unwrap<{ id: number; version_id: number }>(await api.post('/content-funnels', payload))
  },
  async getVersion(id: number): Promise<FunnelVersion> {
    return unwrap<FunnelVersion>(await api.get(`/content-funnels/versions/${id}`))
  },
  async saveVersion(
    id: number,
    payload: Pick<FunnelVersion, 'items' | 'rules' | 'notes' | 'revision'> & {
      name?: string
      description?: string | null
    },
  ) {
    return unwrap<FunnelVersion>(await api.put(`/content-funnels/versions/${id}`, payload))
  },
  async cloneDraft(funnelId: number) {
    return unwrap<{ version_id: number }>(await api.post(`/content-funnels/${funnelId}/drafts`))
  },
  /** Копии воронок с общими изменёнными атрибутами; состав копируется как есть. */
  async duplicateFunnels(
    funnelIds: number[],
    overrides: Record<string, string | number | null>,
  ): Promise<BulkFunnelReport> {
    return unwrap<BulkFunnelReport>(
      await api.post('/content-funnels/duplicate', { funnel_ids: funnelIds, overrides }),
    )
  },

  /** Картинки добавляются в конец состава в порядке их выбора в библиотеке. */
  async appendAssets(funnelIds: number[], assetIds: number[]): Promise<BulkFunnelReport> {
    return unwrap<BulkFunnelReport>(
      await api.post('/content-funnels/append-assets', {
        funnel_ids: funnelIds,
        asset_ids: assetIds,
      }),
    )
  },

  async splitByAudience(
    funnelId: number,
    options?: { dryRun?: boolean; publish?: boolean },
  ): Promise<AudienceSplitReport> {
    return unwrap<AudienceSplitReport>(
      await api.post(`/content-funnels/${funnelId}/audience-split`, {
        dry_run: options?.dryRun ?? false,
        publish: options?.publish ?? true,
      }),
    )
  },
  /** Что лежит в папке воронки в hdd и что расходится с её составом. */
  async listFiles(funnelId: number): Promise<FunnelFilesListing> {
    return unwrap<FunnelFilesListing>(await api.get(`/content-funnels/${funnelId}/files`))
  },
  /** Раскладывает файлы воронки по папке; лишнее уносит в _archive. */
  async syncFiles(
    funnelId: number,
    options?: { archiveOrphans?: boolean; keepHistory?: boolean },
  ): Promise<FunnelFilesSyncReport> {
    return unwrap<FunnelFilesSyncReport>(
      await api.post(`/content-funnels/${funnelId}/files/sync`, {
        archive_orphans: options?.archiveOrphans ?? true,
        keep_history: options?.keepHistory ?? true,
      }),
    )
  },
  /** Бэкфилл: раскладывает файлы всех неархивных воронок. */
  async syncAllFiles(options?: {
    archiveOrphans?: boolean
    keepHistory?: boolean
  }): Promise<FunnelFilesSyncTotals> {
    return unwrap<FunnelFilesSyncTotals>(
      await api.post('/content-funnels/files/sync', {
        archive_orphans: options?.archiveOrphans ?? true,
        keep_history: options?.keepHistory ?? true,
      }),
    )
  },
  /** Принимает файлы, положенные в папку руками, в ЧЕРНОВИК воронки. */
  async adoptFiles(funnelId: number, files: string[]): Promise<FunnelFilesAdoptReport> {
    return unwrap<FunnelFilesAdoptReport>(
      await api.post(`/content-funnels/${funnelId}/files/adopt`, { files }),
    )
  },
  async archiveFunnel(funnelId: number) {
    return unwrap<{ ok: boolean; id: number; status: 'archived' }>(
      await api.post(`/content-funnels/${funnelId}/archive`),
    )
  },
  /** force — согласие отвязать историю конвейера ради удаления. */
  async deleteFunnel(funnelId: number, options?: { force?: boolean }) {
    return unwrap<DeleteFunnelResult>(
      await api.delete(`/content-funnels/${funnelId}`, {
        params: options?.force ? { force: 'true' } : undefined,
      }),
    )
  },
  async validate(id: number) {
    return unwrap<{ ok: boolean; errors: string[] }>(
      await api.post(`/content-funnels/versions/${id}/validate`),
    )
  },
  /** skipTestDrive — подтверждение выпуска непроверенной генерации. */
  async publish(id: number, options?: { skipTestDrive?: boolean }) {
    return unwrap<PublishResult>(
      await api.post(
        `/content-funnels/versions/${id}/publish`,
        options?.skipTestDrive ? { skip_test_drive: true } : {},
      ),
    )
  },
  async resolve(payload: Record<string, unknown>) {
    return unwrap<any>(await api.post('/content-funnels/resolve', payload))
  },
  async testRender(payload: Record<string, unknown>) {
    return unwrap<any>(await api.post('/content-funnels/test-render', payload))
  },
  async listAssets(options?: {
    kind?: ContentAsset['kind']
    includeArchived?: boolean
  }): Promise<ContentAsset[]> {
    const response = await api.get('/content-assets', {
      params: {
        kind: options?.kind,
        include_archived: options?.includeArchived ? 'true' : undefined,
      },
    })
    return unwrap<{ assets: ContentAsset[] }>(response).assets || []
  },
  async uploadStatic(name: string, file: File) {
    return unwrap<ContentAsset>(
      await api.post('/content-assets/static', {
        name,
        filename: file.name,
        file_base64: await fileToBase64(file),
      }),
    )
  },
  async uploadPackage(name: string, base: File, config: File, overlay?: File | null) {
    return unwrap<ContentAsset>(
      await api.post('/content-assets/photogen', {
        name,
        base_base64: await fileToBase64(base),
        config_base64: await fileToBase64(config),
        overlay_base64: overlay ? await fileToBase64(overlay) : undefined,
      }),
    )
  },
  async validatePackage(base: File, config: File, overlay?: File | null) {
    return unwrap<PackageValidation>(
      await api.post('/content-assets/photogen/validate', {
        base_base64: await fileToBase64(base),
        config_base64: await fileToBase64(config),
        overlay_base64: overlay ? await fileToBase64(overlay) : undefined,
      }),
    )
  },
  async archiveAsset(id: number) {
    return unwrap<{ ok: boolean; id: number }>(await api.delete(`/content-assets/${id}`))
  },
  /** Совсем убрать архивный ресурс: запись и файлы. Отдельный путь — чтобы не
   * спутать с архивацией, которая висит на DELETE того же ресурса. */
  async deleteAsset(id: number) {
    return unwrap<{ ok: boolean; id: number; removed_files: number }>(
      await api.delete(`/content-assets/${id}/purge`),
    )
  },

  // ── Обмен между окружениями ───────────────────────────────────────
  async previewExport(funnelIds: number[]): Promise<ExportPreview> {
    return unwrap<ExportPreview>(
      await api.post('/content-funnels/export/preview', { funnel_ids: funnelIds }),
    )
  },

  /** Скачивает ZIP; ошибки бэкенда приходят JSON-ом внутри blob. */
  async exportArchive(funnelIds: number[], includeFiles: boolean): Promise<Blob> {
    const response = await api.post(
      '/content-funnels/export',
      { funnel_ids: funnelIds, include_files: includeFiles },
      { responseType: 'blob' },
    )
    const blob = response.data as Blob
    if (blob.type && blob.type.includes('application/json')) {
      const text = await blob.text()
      const message =
        (() => {
          try {
            const parsed = JSON.parse(text)
            return parsed?.error?.message || parsed?.message
          } catch {
            return ''
          }
        })() || 'Не удалось собрать архив'
      throw new Error(message)
    }
    return blob
  },

  async importArchive(payload: {
    zip_base64: string
    dry_run?: boolean
    publish?: boolean
    on_existing?: 'new_version' | 'skip'
  }): Promise<ImportReport> {
    return unwrap<ImportReport>(await api.post('/content-funnels/import', payload))
  },
}
